// hooks/useModalForm.js
import { Form } from "antd";
import { useState } from "react";
import { parseDefaultValue } from "../helpers/formFieldUtils";

/**
 * useModalForm Features & Field Config Options
 *
 * Each field in `modalConfigs` can support the following:
 *
 * 1. **Basic config**
 *    - `name`: string (unique field key)
 *    - `label`: string (field label)
 *    - `type`: "text" | "number" | "date" | "select" | "textarea" | "section" | "empty"
 *    - `required`: boolean
 *    - `rules`: antd form validation rules
 *    - `defaultValue`: initial value (auto-populated if provided in defaultValues)
 *
 * 2. **Dynamic Options**
 *    - `options`: static array of `{ key, label }`
 *    - `fetchOptions`: async function to fetch options
 *    - `fetchKey`: unique cache key for reusing fetched results
 *
 * 3. **Conditional Disable**
 *    - `readOnly`: boolean (field is always read-only)
 *    - `disabled`: boolean (field is always disabled)
 *    - `disableOnEdit`: boolean (auto-disable in edit mode)
 *    - `disableOnView`: boolean (auto-disable in view mode)
 *    - `disableWhen`: (data) => boolean → disables field if returns true based on defaultValues/editData
 *
 * 4. **Conditional Show/Hide**
 *    - `showForPersona`: array of persona keys → field shown only for matching persona
 *    - `hideForPersona`: array of persona keys → field hidden for these personas
 *    - `showWhen`: (formValues) => boolean → field shown only when condition is true
 *
 * 5. **Dynamic Rules**
 *    - `dynamicRules`: (formValues) => [rules] → runtime rules based on form state
 *    - Example: make field required only if another field value = "Processed"
 *
 * 6. **Cross-field Validation**
 *    - `confirmOf`: "otherFieldName" → ensures this field matches another field
 *    - Example: "Re-enter Account No." matches "Account No."
 *    - `errorMessage`: custom error message for mismatch
 *
 * 7. **Layout**
 *    - `colspan`: number (span of column in grid layout)
 *    - `divider`: boolean (for section type, show divider)
 *    - `align`: string (alignment for section titles)
 *
 * -----------------------------------------------------
 * Hook APIs:
 *   - `openModal(type, mode, title, defaultValues)`
 *       mode = "add" | "edit" | "view"
 *   - `closeModal()`
 *   - `handleValuesChange(changedValues, allValues)`
 *   - `state` → contains { open, type, mode, title, fields }
 *   - `appform` → AntD Form instance
 *
 */

// persistent cache across calls
const optionsCache = new Map();

export function useModalForm(modalConfigs, dependencies = {}) {
  const [appform] = Form.useForm();
  const [state, setState] = useState({
    open: false,
    type: null,
    mode: "add",
    fields: [],
    title: "Modal",
  });

  const openModal = async (
    type,
    mode = "add",
    title = "Modal",
    defaultValues = {}
  ) => {
    // Base field mapping with defaultValue
    let fields = modalConfigs[type].map((field) => {
      let updatedField = {
        ...field,
        defaultValue: parseDefaultValue(field, defaultValues[field.name]),
      };

      // 🔒 Apply disable flags depending on mode
      if (mode === "edit" && field.disableOnEdit) {
        updatedField.disabled = true;
      }
      if (mode === "view" && field.disableOnView) {
        updatedField.disabled = true;
      }

      // 🔒 Apply disableWhen (based on incoming data)
      if (field.disableWhen && typeof field.disableWhen === "function") {
        if (field.disableWhen(defaultValues)) {
          updatedField.disabled = true;
        }
      }

      // Add confirmOf validation dynamically
      let rules = field.rules || [];
      if (field.confirmOf) {
        rules = [
          ...rules,
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue(field.confirmOf) === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(field.errorMessage || `${field.label} must match`)
              );
            },
          }),
        ];
      }
      updatedField.rules = rules;

      return updatedField;
    });

    // Resolve dynamic options with caching
    const promises = fields.map(async (field) => {
      if (field.fetchOptions) {
        const key = field.fetchKey || field.name;

        if (optionsCache.has(key)) {
          return { ...field, options: optionsCache.get(key) };
        }

        try {
          const res = await field.fetchOptions();
          optionsCache.set(key, res || []);
          return { ...field, options: res || [] };
        } catch (err) {
          console.error("Error fetching options for", field.name, err);
          return { ...field, options: [] };
        }
      }
      return field;
    });

    fields = await Promise.all(promises);

    if (mode === "add") {
      appform.resetFields();
    } else {
      const normalizedValues = {};
      modalConfigs[type].forEach((field) => {
        normalizedValues[field.name] = parseDefaultValue(
          field,
          defaultValues[field.name]
        );
      });
      appform.setFieldsValue(normalizedValues);

      // 🔑 Run dependencies for prefilled values (edit mode)
      for (const key of Object.keys(normalizedValues)) {
        if (dependencies[key] && normalizedValues[key] != null) {
          try {
            const updates = await dependencies[key](
              normalizedValues[key],
              normalizedValues
            );

            if (updates?.valueUpdates) {
              const safeUpdates = { ...updates.valueUpdates };
              delete safeUpdates[key];
              appform.setFieldsValue(safeUpdates);
            }

            if (updates?.fieldUpdates) {
              fields = fields.map((f) =>
                updates.fieldUpdates[f.name]
                  ? { ...f, ...updates.fieldUpdates[f.name] }
                  : f
              );
            }
          } catch (err) {
            console.error(`Dependency failed for ${key}`, err);
          }
        }
      }
    }

    setState({ open: true, type, mode, title, fields });
  };

  const closeModal = () => {
    appform.resetFields();
    setState((prev) => ({ ...prev, open: false }));
  };

  const handleValuesChange = async (changedValues, allValues) => {
    for (const changedKey of Object.keys(changedValues)) {
      if (dependencies[changedKey]) {
        const updates = await dependencies[changedKey](
          changedValues[changedKey],
          allValues
        );

        if (updates?.valueUpdates) {
          const safeUpdates = { ...updates.valueUpdates };
          delete safeUpdates[changedKey];
          appform.setFieldsValue(safeUpdates);
        }

        if (updates?.fieldUpdates) {
          setState((prev) => ({
            ...prev,
            fields: prev.fields.map((f) =>
              updates.fieldUpdates[f.name]
                ? { ...f, ...updates.fieldUpdates[f.name] }
                : f
            ),
          }));
        }
      }
    }
  };

  return {
    appform,
    state,
    openModal,
    closeModal,
    handleValuesChange,
  };
}
